/**
 * Custom hook for managing page editing functionalities.
 *
 * @param {number | null} pageBeingEdited - The index of the page currently being edited, or null if no page is being edited.
 * @param {(page: number | null) => void} setPageBeingEdited - Function to set the index of the page being edited.
 * @param {string} pageEditorContent - The HTML content of the page editor.
 * @param {(content: string) => void} setPageEditorContent - Function to set the HTML content of the page editor.
 * @param {number} pageEditorColumns - The number of columns in the page editor.
 * @param {(columns: number) => void} setPageEditorColumns - Function to set the number of columns in the page editor.
 * @param {boolean} showPageEditor - Boolean indicating whether the page editor is visible.
 * @param {(show: boolean) => void} setShowPageEditor - Function to set the visibility of the page editor.
 * @param {any} pageConfigs - Configuration object for the pages.
 * @param {any} setPageConfigs - Function to set the configuration object for the pages.
 * @param {number} selectedElement - The index of the currently selected element.
 * @param {(element: number | null) => void} setSelectedElement - Function to set the index of the selected element.
 * @param {string} tempContent - Temporary content for the editor.
 * @param {(content: string) => void} setTempContent - Function to set the temporary content for the editor.
 * @param {boolean} showEditor - Boolean indicating whether the editor is visible.
 * @param {(show: boolean) => void} setShowEditor - Function to set the visibility of the editor.
 * @param {any} DataFrontPage - Array containing the data for the front page elements.
 * @param {any} setDataFrontPage - Function to set the data for the front page elements.
 * @param {any} questionPages - Array containing the data for the question pages.
 * @param {any} setQuestionPages - Function to set the data for the question pages.
 * @param {any} pageColumns - Array containing the number of columns for each page.
 * @param {any} setPageColumns - Function to set the number of columns for each page.
 * @param {number} numeroColunas - Default number of columns for a page.
 * @param {number} fontSizeQuestoes - Font size for the questions.
 * @param {number} QUESTION_MARGIN - Margin for the questions.
 * @param {any} recalcPages - Function to recalculate the pages in normal mode.
 * @param {any} recalcPagesEdit - Function to recalculate the pages in edit mode.
 * @param {boolean} editMode - Boolean indicating whether the app is in edit mode.
 *
 * @returns {object} An object containing functions to handle various page editing operations.
 */
export function usePageEditor(
  pageBeingEdited: number | null,
  setPageBeingEdited: (page: number | null) => void,
  pageEditorContent: string,
  setPageEditorContent: (content: string) => void,
  pageEditorColumns: number,
  setPageEditorColumns: (columns: number) => void,
  showPageEditor: boolean,
  setShowPageEditor: (show: boolean) => void,
  pageConfigs: any,
  setPageConfigs: any,
  selectedElement: number,
  setSelectedElement: (element: number | null) => void,
  tempContent: string,
  setTempContent: (content: string) => void,
  showEditor: boolean,
  setShowEditor: (show: boolean) => void,
  DataFrontPage: any,
  setDataFrontPage: any,
  questionPages: any,
  setQuestionPages: any,
  pageColumns: any,
  setPageColumns: any,
  numeroColunas: number,
  fontSizeQuestoes: number | "default",
  QUESTION_MARGIN: number,
  recalcPages: any,
  recalcPagesEdit: any,
  recalcPagesFromQuestionPage: any,
  editMode?: boolean
) {
  const handleAddElement = () => {
    const newElement = {
      element: "<div>Novo elemento</div>",
      x: 50,
      y: 50,
      width: 200,
      height: 100,
    };
    setDataFrontPage([...DataFrontPage, newElement]);
    setSelectedElement(DataFrontPage.length);
    setTempContent("<div>Novo elemento</div>");
    setShowEditor(true);
  };

  /**
   * Handles the change in the number of columns for the page being edited.
   */
  const handleColumnChange = (newColumns: number) => {
    if (pageBeingEdited !== null) {
      setPageEditorColumns(newColumns);
      setPageConfigs((prev: any) => ({
        ...prev,
        [pageBeingEdited]: { ...prev[pageBeingEdited], columns: newColumns },
      }));
    }
  };

  /**
   * Handles the editing of a selected element.
   */
  const handleEditElement = () => {
    if (selectedElement !== null) {
      setTempContent(DataFrontPage[selectedElement].element);
      setShowEditor(true);
    }
  };

  /**
   * Handles the change event for the editor.
   */
  const handleEditorChange = (content: string) => {
    setTempContent(content);
  };

  const handleConfirmEdit = () => {
    if (selectedElement === null || selectedElement >= DataFrontPage.length)
      return;
    const updated = [...DataFrontPage];
    updated[selectedElement] = {
      ...updated[selectedElement],
      element: tempContent,
    };
    setDataFrontPage(updated);
    setShowEditor(false);
  };

  /**
   * Handles the cancellation of the edit operation.
   */
  const handleCancelEdit = () => {
    setShowEditor(false);
    setTempContent("");
  };

  /**
   * Handles the removal of the selected element from the DataFrontPage array.
   */
  const handleRemoveElement = () => {
    if (selectedElement === null) return;
    const updated = DataFrontPage.filter(
      (_: any, idx: any) => idx !== selectedElement
    );
    setDataFrontPage(updated);
    setSelectedElement(null);
  };

  /**
   * Handles the stop event of a drag action for the cover element.
   */
  const handleDragStopCover = (
    index: number,
    position: { x: number; y: number }
  ) => {
    const updated = [...DataFrontPage];
    updated[index] = {
      ...updated[index],
      x: position.x,
      y: position.y,
    };
    setDataFrontPage(updated);
  };

  /**
   * Handles the resizing of the cover element.
   */
  const handleResizeCover = (
    index: number,
    ref: HTMLElement,
    position: { x: number; y: number }
  ) => {
    const updated = [...DataFrontPage];
    updated[index] = {
      ...updated[index],
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: position.x,
      y: position.y,
    };
    setDataFrontPage(updated);
  };

  /**
   * Handles inline editing of a specific element within a page.
   */
  const handleInlineEdit = (
    pageIndex: number,
    elementIndex: number,
    newHtml: string
  ) => {
    const updatedPages = [...questionPages];
    const element = updatedPages[pageIndex].elements[elementIndex];
    element.element = newHtml;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = newHtml;
    tempDiv.style.width = `${element.width}px`;
    tempDiv.style.fontSize = `${fontSizeQuestoes}px`;
    tempDiv.style.position = "absolute";
    tempDiv.style.visibility = "hidden";
    document.body.appendChild(tempDiv);

    const measuredHeight = tempDiv.offsetHeight + QUESTION_MARGIN;
    document.body.removeChild(tempDiv);
    element.height = measuredHeight;

    setQuestionPages(updatedPages);
    // Em modo de edição, pode-se optar por recalcPagesEdit, mas aqui chamamos sempre o recalcPages
    recalcPages();
  };

  /**
   * Handles the stop event of a drag action for a question element.
   */
  const handleDragStopQuestion = (
    pageIndex: number,
    elementIndex: number,
    position: { x: number; y: number }
  ) => {
    const updated = [...questionPages];
    updated[pageIndex].elements[elementIndex] = {
      ...updated[pageIndex].elements[elementIndex],
      x: position.x,
      y: position.y,
    };
    setQuestionPages(updated);
  };

  /**
   * Handles the resizing of a question element on a specific page.
   */
  const handleResizeQuestion = (
    pageIndex: number,
    elementIndex: number,
    ref: HTMLElement,
    position: { x: number; y: number }
  ) => {
    const updated = [...questionPages];
    updated[pageIndex].elements[elementIndex] = {
      ...updated[pageIndex].elements[elementIndex],
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: position.x,
      y: position.y,
    };
    setQuestionPages(updated);
  };

  /**
   * Handles the editing of a specific page by its index.
   */
  const handleEditPage = (pageIndex: number) => {
    setPageBeingEdited(pageIndex);
    let combinedHTML = "";
    questionPages[pageIndex].elements.forEach((el: any) => {
      combinedHTML += el.element;
    });

    const currentColumns = pageColumns[pageIndex] ?? numeroColunas;
    setPageEditorContent(combinedHTML);
    setPageEditorColumns(currentColumns);
    setShowPageEditor(true);
  };

  /**
   * Handles the confirmation of editing a page.
   *
   * Instead of updating the questionPages directly,
   * this function updates the configuration (pageConfigs and pageColumns)
   * and then recalculates the layout using either recalcPagesEdit (if in edit mode)
   * or recalcPages (if in normal mode), ensuring that the elements
   * are repositioned according to the new column configuration.
   */
  const handleConfirmEditPage = () => {
    if (pageBeingEdited === null) return;

    // Atualiza as configurações da página editada
    setPageConfigs((prev: any) => ({
      ...prev,
      [pageBeingEdited]: { columns: pageEditorColumns },
    }));

    // Atualiza o array de colunas para as páginas
    const updatedPageColumns = [...pageColumns];
    updatedPageColumns[pageBeingEdited] = pageEditorColumns;
    setPageColumns(updatedPageColumns);

    // Fecha o editor de página e limpa o conteúdo temporário
    setShowPageEditor(false);
    setPageBeingEdited(null);
    setPageEditorContent("");

    console.log("questionPages 1: ", questionPages);
    console.log("Edit Mode: ", editMode);

    if (editMode) {
      console.log("Recalculando páginas em modo de edição...");
      console.log("questionPages 2: ", questionPages);
      if (!questionPages || questionPages.length === 0) {
        console.log(
          "Nenhuma página encontrada para editar, gerando páginas a partir da prova..."
        );
        // Se questionPages estiver vazio, gera a partir da prova para não perder os dados
        recalcPages();
        return;
      }
      recalcPagesFromQuestionPage();
    } else {
      //recalcPages();
      console.log("TO AQUI");
    }
  };



  /**
   * Handles the cancellation of the page editing process.
   */
  const handleCancelEditPage = () => {
    setShowPageEditor(false);
    setPageBeingEdited(null);
    setPageEditorContent("");
  };

  return {
    handleAddElement,
    handleColumnChange,
    handleEditElement,
    handleEditorChange,
    handleConfirmEdit,
    handleCancelEdit,
    handleRemoveElement,
    handleDragStopCover,
    handleResizeCover,
    handleInlineEdit,
    handleDragStopQuestion,
    handleResizeQuestion,
    handleEditPage,
    handleConfirmEditPage,
    handleCancelEditPage,
  };
}
